import React, { useState } from 'react';
import { Globe, Menu, X } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import { useLanguage, LANGUAGES, Language } from '../contexts/LanguageContext';
import { NoorBadge } from './NoorLogo';
type HeaderStrings = {
  howItWorks: string;
  pricing: string;
  faq: string;
  contact: string;
  start: string;
  slogan: string;
};
// EN/ES finalized. HT/VI = best effort, pending native review.
const T: Record<Language, HeaderStrings> = {
  en: {
    howItWorks: 'How it Works',
    pricing: 'Pricing',
    faq: 'FAQ',
    contact: 'Contact',
    start: 'Start Application',
    slogan: 'We prepare. You submit. Simple.'
  },
  es: {
    howItWorks: 'Cómo funciona',
    pricing: 'Precios',
    faq: 'Preguntas',
    contact: 'Contacto',
    start: 'Comenzar',
    slogan: 'Nosotros preparamos. Usted envía. Fácil.'
  },
  ht: {
    howItWorks: 'Kijan li mache',
    pricing: 'Pri',
    faq: 'Kesyon',
    contact: 'Kontak',
    start: 'Kòmanse',
    slogan: 'Nou prepare. Ou soumèt. Senp.'
  },
  vi: {
    howItWorks: 'Cách hoạt động',
    pricing: 'Bảng giá',
    faq: 'Câu hỏi',
    contact: 'Liên hệ',
    start: 'Bắt đầu',
    slogan: 'Chúng tôi chuẩn bị. Quý vị nộp. Đơn giản.'
  }
};

// El botón principal ("Start Application" / "Comenzar") lleva directo al pedido del
// paquete de $99 (1 persona). El cliente puede cambiar de paquete en esa página.
const START_TO = '/pedido?plan=solo';

// Short autonyms shown on the desktop language switcher.
const NAV_LANG_LABEL: Record<string, string> = {
  en: 'English',
  es: 'Español',
  ht: 'Kreyòl',
  vi: 'Tiếng Việt'
};

// Scroll the page back to the very top. Used by the logo so that clicking it
// always returns to the top — even when you are ALREADY on the home page, where
// a normal <Link to="/"> would not change the route and therefore would not
// trigger the route-change scroll reset.
function scrollToTop() {
  try {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  } catch {
    window.scrollTo(0, 0);
  }
  if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
}
function BrandLockup() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  return <Link to="/" aria-label="Noor Passport Services — home" onClick={scrollToTop} className="flex items-center gap-3 min-w-0" data-id="element-228" data-mp="1ho1fsx0i0xxs">
      <NoorBadge size={44} className="flex-shrink-0" data-id="element-229" data-mp="1ml0d1ofgll07" />
      <span className="flex flex-col leading-tight min-w-0" data-id="element-230" data-mp="18mtwpm6k03oa">
        <span className="font-heading font-bold text-brand-accent tracking-tight text-lg sm:text-2xl lg:text-3xl" data-id="element-231" data-mp="18pg2bg9vw93v">
          Noor Passport Services
        </span>
        <span className="italic text-brand-accent text-xs sm:text-sm leading-tight" style={{
        fontFamily: "Georgia, 'Times New Roman', serif"
      }} data-id="element-232" data-mp="yq7aasykx4k5">
          {t.slogan}
        </span>
      </span>
    </Link>;
}
export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    language,
    setLanguage
  } = useLanguage();
  const t = T[language];
  const navItems = [{
    to: '/how-it-works',
    label: t.howItWorks
  }, {
    to: '/pricing',
    label: t.pricing
  }, {
    to: '/faq',
    label: t.faq
  }, {
    to: '/contact',
    label: t.contact
  }];
  return <header className="w-full bg-brand-bg border-b border-brand-border" data-id="element-233" data-mp="w610i4naajvo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-234" data-mp="rcpyqhxbwtam">
        {/* Row 1: brand + (desktop) language switcher & CTA / (mobile) controls */}
        <div className="flex justify-between items-center gap-4 h-20" data-id="element-235" data-mp="156z5oq9gkxfu">
          <BrandLockup data-id="element-236" data-mp="105jgcm0904rn" />

          {/* Desktop right-side controls */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0" data-id="element-237" data-mp="1f2akf8kva4nl">
            {/* Language Switcher — visible buttons, no hidden menu */}
            <div className="flex items-center gap-1 rounded-full border border-brand-border bg-brand-surface pl-2 pr-1 py-1" role="group" aria-label="Select your language / Seleccione su idioma" data-id="element-238" data-mp="1a2efn74ci96z">
              <Globe className="w-4 h-4 text-brand-textSecondary mr-0.5" aria-hidden="true" data-id="element-239" data-mp="22xm0kpus4ujd" />
              {LANGUAGES.map(lang => {
              const active = lang.code === language;
              return <button key={lang.code} lang={lang.code} onClick={() => setLanguage(lang.code)} aria-pressed={active} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-1 focus:ring-offset-brand-bg ${active ? 'bg-brand-textPrimary text-white' : 'text-brand-textSecondary hover:bg-brand-bg'}`} data-id="element-240" data-mp="2gygdnlsrvjod">
                    {NAV_LANG_LABEL[lang.code]}
                  </button>;
            })}
            </div>

            <Link to={START_TO} className="bg-brand-accent hover:bg-brand-accentHover text-brand-textPrimary px-6 py-2.5 rounded-full font-semibold text-sm transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 focus:ring-offset-brand-bg" data-id="element-241" data-mp="15i8615yhhi5u">
              {t.start}
            </Link>
          </div>

          {/* Mobile / tablet controls */}
          <div className="flex lg:hidden items-center gap-2 flex-shrink-0" data-id="element-242" data-mp="k23jp96lv7vg">
            <button onClick={() => setIsMobileMenuOpen(true)} className="text-brand-textSecondary hover:text-brand-textPrimary p-2 flex items-center gap-1" aria-label="Change language / Cambiar idioma" data-id="element-243" data-mp="f1o56eal7ubw">
              <Globe className="w-5 h-5" data-id="element-244" data-mp="19rgkk84o2k9" />
              <span className="text-xs font-semibold" data-id="element-245" data-mp="1b2mapl4vke0">
                {language.toUpperCase()}
              </span>
            </button>
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-brand-textPrimary p-2" aria-expanded={isMobileMenuOpen} aria-label="Toggle menu" data-id="element-246" data-mp="3ngpsfoysnk5u">
              {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-247" data-mp="3iq3uorvhf3lv" /> : <Menu className="w-6 h-6" data-id="element-248" data-mp="33vzb15iurdpk" />}
            </button>
          </div>
        </div>

        {/* Row 2: main navigation, moved below the brand (desktop) */}
        <nav className="hidden lg:flex items-center justify-center gap-8 h-12 border-t border-brand-border" data-id="element-249" data-mp="dnwty819j7j">
          {navItems.map(item => <NavLink key={item.to} to={item.to} className={({
          isActive
        }) => `text-base font-medium transition-colors ${isActive ? 'text-brand-textPrimary' : 'text-brand-textSecondary hover:text-brand-textPrimary'}`} data-id="element-250" data-mp="5cim9j0ia60m">
              {item.label}
            </NavLink>)}
        </nav>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && <div className="lg:hidden bg-white border-b border-brand-border px-4 pt-3 pb-6 space-y-4 shadow-premium absolute w-full z-50" data-id="element-251" data-mp="28tnkymtah0jj">
          {/* Language — shown first and prominently for non-English speakers */}
          <div data-id="element-252" data-mp="2iiyrc1osjr3q">
            <p className="px-1 pb-2 text-xs font-semibold uppercase tracking-wide text-brand-textSecondary flex items-center gap-2" data-id="element-253" data-mp="2iip3okr7xw17">
              <Globe className="w-4 h-4" aria-hidden="true" data-id="element-254" data-mp="2dn9q7tx4i25b" />
              Language · Idioma · Lang · Ngôn ngữ
            </p>
            <div className="grid grid-cols-2 gap-2" role="group" aria-label="Select your language" data-id="element-255" data-mp="1kwrn4sweg0rw">
              {LANGUAGES.map(lang => {
            const active = lang.code === language;
            return <button key={lang.code} lang={lang.code} aria-pressed={active} onClick={() => setLanguage(lang.code)} className={`px-3 py-3 rounded-xl text-base font-medium text-left transition-colors ${active ? 'bg-brand-textPrimary text-white' : 'bg-brand-bg text-brand-textPrimary hover:bg-brand-border/60'}`} data-id="element-256" data-mp="3ab68r4c4f7j6">
                    {lang.label}
                  </button>;
          })}
            </div>
          </div>

          {/* Navigation */}
          <div className="space-y-1 border-t border-brand-border pt-3" data-id="element-257" data-mp="1idmxqzo0fext">
            {navItems.map(item => <NavLink key={item.to} to={item.to} onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-3 text-lg font-medium text-brand-textPrimary hover:bg-brand-bg rounded-md" data-id="element-258" data-mp="1rci1m53l0cxy">
                {item.label}
              </NavLink>)}
          </div>

          {/* CTA */}
          <Link to={START_TO} onClick={() => setIsMobileMenuOpen(false)} className="block w-full text-center bg-brand-accent hover:bg-brand-accentHover text-brand-textPrimary px-6 py-3 rounded-full font-semibold text-base transition-colors shadow-sm" data-id="element-259" data-mp="1zn1pzsac9fu">
            {t.start}
          </Link>
        </div>}
    </header>;
}